/*
 * Make My Day cart drawer — <mm-cart-drawer>.
 *
 * One controller for the drawer rendered by sections/mm-cart-drawer.liquid:
 * - opening/closing (header cart link, theme add-to-cart, theme editor), focus handling;
 * - line quantity/removal, each line's Subscribe & Save toggle, upsell and bundle promo adds;
 * - progress bar milestones and their rewards (free product, discount code);
 * - staying in sync with the theme: the drawer bundles its section in the theme's cart requests
 *   (`cart:prepare-bundled-sections`), re-renders on `cart:change`, and announces its own changes through `cart:change`
 *   so the header cart count and other theme components update.
 *
 * Every cart request goes through a queue: Shopify locks the cart per request, so overlapping requests are slower and
 * can apply out of order.
 */
(() => {
  if (window.customElements.get('mm-cart-drawer')) {
    return;
  }

  const LINE_DEBOUNCE_MS = 350;
  const MESSAGE_MS = 3500;
  const ERROR_MS = 6000;
  const REWARD_PROPERTY = '_progressbar_promotion';
  const FILL_GAP = 6; // percentage points the fill stops short of a locked milestone
  const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const routeRoot = () => window.Shopify?.routes?.root || '/';
  const routeUrl = (path) => `${routeRoot()}${path}`;
  const isRendered = (element) => element.getClientRects().length > 0;

  const postJSON = async (path, body) => {
    const response = await fetch(routeUrl(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));

    if (!response.ok || data.status) {
      const error = new Error(data.description || data.message || 'Something went wrong. Please try again.');
      error.data = data;
      throw error;
    }

    return data;
  };

  const getCart = () => fetch(routeUrl('cart.js'), { headers: { Accept: 'application/json' }, cache: 'no-store' }).then((response) => response.json());

  class MMCartDrawer extends HTMLElement {
    #abortController = null;
    #queue = Promise.resolve();
    #pendingTasks = 0;
    #lineTimers = new Map();
    #cart = null;
    #reachedKeys = null;
    #pendingRewardIds = new Set();
    #failedRewardIds = new Set();
    #isSyncingRewards = false;
    #messageTimer = null;
    #errorTimer = null;
    #previousFocus = null;

    connectedCallback() {
      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      this.sectionId = this.dataset.sectionId;
      this.panel = this.querySelector('.mm-cart-drawer__panel');

      document.addEventListener('click', (event) => this.#onDocumentClick(event), { signal });
      this.addEventListener('click', (event) => this.#onClick(event), { signal });
      this.addEventListener('change', (event) => this.#onChange(event), { signal });
      this.addEventListener('keydown', (event) => this.#onKeydown(event), { signal });

      // Theme cart requests (product forms, quick buy, cart page) also render this section, and announce the result
      document.addEventListener('cart:prepare-bundled-sections', (event) => {
        const sections = event.detail?.sections;

        if (Array.isArray(sections) && !sections.includes(this.sectionId)) {
          sections.push(this.sectionId);
        }
      }, { signal });
      document.addEventListener('cart:change', (event) => this.#onThemeCartChange(event), { signal });
      document.addEventListener('cart:refresh', () => this.refresh(), { signal });
      window.addEventListener('pageshow', (event) => event.persisted && this.refresh(), { signal });

      if (window.Shopify?.designMode) {
        const section = this.closest('.shopify-section');
        section?.addEventListener('shopify:section:select', () => this.open(), { signal });
        section?.addEventListener('shopify:section:deselect', () => this.close(), { signal });
        section?.addEventListener('shopify:block:select', () => this.open(), { signal });
      }

      this.#setupUpsellArrows(signal);

      // The progress bar and recommendations need the cart JSON, but not for the first paint
      const loadCart = () => getCart().then((cart) => {
        this.#cart = cart;
        this.#updateDerived(cart);
      }).catch(() => {});

      if ('requestIdleCallback' in window) {
        requestIdleCallback(loadCart, { timeout: 2500 });
      } else {
        setTimeout(loadCart, 1200);
      }
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#lineTimers.forEach((timer) => clearTimeout(timer));
      clearTimeout(this.#messageTimer);
      clearTimeout(this.#errorTimer);

      if (this.classList.contains('is-open')) {
        document.documentElement.classList.remove('mm-cart-open');
      }
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    /* ------------------------------------------------------------------------------------------------ open / close */

    open(trigger) {
      if (this.isOpen) {
        return;
      }

      this.#previousFocus = trigger instanceof HTMLElement ? trigger : document.activeElement;

      void this.offsetWidth; // commit the closed state so the panel slides in
      this.classList.add('is-open');
      this.panel.setAttribute('role', 'dialog');
      this.panel.setAttribute('aria-modal', 'true');
      document.documentElement.classList.add('mm-cart-open');
      this.#triggers.forEach((element) => element.setAttribute('aria-expanded', 'true'));

      setTimeout(() => this.panel.focus({ preventScroll: true }), 60);

      // The fill is measured against the milestones, which only have a size once the panel is displayed
      setTimeout(() => {
        if (this.#cart) {
          this.#updateDerived(this.#cart, { sideEffects: true });
        } else {
          getCart().then((cart) => {
            this.#cart = cart;
            this.#updateDerived(cart, { sideEffects: true });
          }).catch(() => {});
        }
      }, 80);

      this.dispatchEvent(new CustomEvent('mm-cart:open', { bubbles: true }));
    }

    close() {
      if (!this.isOpen) {
        return;
      }

      this.classList.remove('is-open');
      this.panel.removeAttribute('role');
      this.panel.removeAttribute('aria-modal');
      document.documentElement.classList.remove('mm-cart-open');
      this.#triggers.forEach((element) => element.setAttribute('aria-expanded', 'false'));

      if (this.#previousFocus instanceof HTMLElement && this.#previousFocus.isConnected) {
        this.#previousFocus.focus({ preventScroll: true });
      }

      this.dispatchEvent(new CustomEvent('mm-cart:close', { bubbles: true }));
    }

    get #triggers() {
      return Array.from(document.querySelectorAll(`[aria-controls="${this.id}"], [data-mm-cart-trigger]`));
    }

    /* ------------------------------------------------------------------------------------------------------ events */

    #onDocumentClick(event) {
      const trigger = event.target instanceof Element ? event.target.closest(`[aria-controls="${this.id}"], [data-mm-cart-trigger]`) : null;

      if (!trigger || this.contains(trigger) || this.dataset.cartType === 'page') {
        return;
      }

      event.preventDefault();
      this.open(trigger);
    }

    #onClick(event) {
      const target = event.target instanceof Element ? event.target : null;

      if (!target) {
        return;
      }

      if (target.closest('[data-mm-cart-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      const quantityButton = target.closest('[data-mm-qty]');
      if (quantityButton) {
        event.preventDefault();
        this.#stepQuantity(quantityButton);
        return;
      }

      const removeButton = target.closest('[data-mm-cart-remove]');
      if (removeButton) {
        event.preventDefault();
        this.#removeLine(removeButton.closest('[data-mm-cart-line]'));
        return;
      }

      const upsellArrow = target.closest('[data-upsell-arrow]');
      if (upsellArrow) {
        event.preventDefault();
        this.#scrollUpsell(upsellArrow.dataset.upsellArrow === 'prev' ? -1 : 1);
        return;
      }

      const addButton = target.closest('[data-mm-cart-add]');
      if (addButton) {
        event.preventDefault();
        this.#addFromButton(addButton);
      }
    }

    #onChange(event) {
      const target = event.target;

      if (target.matches?.('[data-mm-qty-input]')) {
        const quantity = this.#clampQuantity(target, parseInt(target.value, 10));
        target.value = quantity;
        this.#scheduleLineChange(target.closest('[data-mm-cart-line]'), quantity);
      } else if (target.matches?.('[data-mm-subscribe-line]')) {
        this.#toggleLineSubscription(target);
      }
    }

    #onKeydown(event) {
      if (!this.isOpen) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusable = Array.from(this.panel.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isRendered);

      if (focusable.length === 0) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    #onThemeCartChange(event) {
      const detail = event.detail || {};

      if (detail.mmCart) {
        return; // our own announcement
      }

      const cart = detail.cart;
      const html = cart?.sections?.[this.sectionId];

      if (html) {
        this.#render(html);
        this.#cart = cart;
        this.#updateDerived(cart, { sideEffects: true, announce: true });
      } else {
        this.refresh();
      }

      const shouldOpen = detail.baseEvent === 'variant:add' && (this.dataset.cartType === 'drawer' || detail.onSuccessDo === 'force_open_drawer');

      if (shouldOpen) {
        this.open();
      }
    }

    /* ------------------------------------------------------------------------------------------------------- lines */

    #stepQuantity(button) {
      const line = button.closest('[data-mm-cart-line]');
      const input = line?.querySelector('[data-mm-qty-input]');

      if (!input) {
        return;
      }

      const step = parseInt(input.step, 10) || 1;
      const current = parseInt(input.value, 10) || 0;
      const next = this.#clampQuantity(input, button.dataset.mmQty === 'plus' ? current + step : current - step);

      if (next === current) {
        return;
      }

      input.value = next;
      this.#scheduleLineChange(line, next);
    }

    #clampQuantity(input, value) {
      let quantity = Number.isFinite(value) && value > 0 ? value : 0;
      const max = parseInt(input.max, 10);

      if (Number.isFinite(max)) {
        quantity = Math.min(quantity, max);
      }

      return quantity;
    }

    #scheduleLineChange(line, quantity) {
      if (!line) {
        return;
      }

      const key = line.dataset.key;

      line.classList.add('is-pending');
      clearTimeout(this.#lineTimers.get(key));
      this.#lineTimers.set(key, setTimeout(() => {
        this.#lineTimers.delete(key);

        if (quantity === 0) {
          line.classList.add('is-removing');
        }

        this.#changeLine(key, quantity);
      }, quantity === 0 ? 150 : LINE_DEBOUNCE_MS));
    }

    #removeLine(line) {
      if (!line) {
        return;
      }

      clearTimeout(this.#lineTimers.get(line.dataset.key));
      line.classList.add('is-removing');
      this.#changeLine(line.dataset.key, 0);
    }

    #changeLine(key, quantity) {
      return this.#enqueue(async () => {
        try {
          const cart = await postJSON('cart/change.js', {
            id: key,
            quantity,
            sections: this.sectionId,
            sections_url: window.location.pathname,
          });

          this.#applyCart(cart, cart.sections);
        } catch (error) {
          await this.refresh();
          this.#showLineError(key, error.message);
        }
      });
    }

    #showLineError(key, message) {
      const line = Array.from(this.querySelectorAll('[data-mm-cart-line]')).find((item) => item.dataset.key === key);
      const target = line?.querySelector('.mm-cart-item__error');

      if (target) {
        target.textContent = message;
      } else {
        this.#showError(message);
      }
    }

    /* ---------------------------------------------------------------------------------------------- subscriptions */

    #toggleLineSubscription(toggle) {
      const line = toggle.closest('[data-mm-cart-line]');

      if (!line) {
        return;
      }

      const subscribe = toggle.checked;
      const key = line.dataset.key;
      let plans = [];

      try {
        plans = JSON.parse(line.dataset.sellingPlans || '[]');
      } catch (error) {
        plans = [];
      }

      const frequency = (this.querySelector('mm-cart-items')?.dataset.defaultFrequency || 'first').toLowerCase();
      const plan = (frequency !== 'first' && plans.find((item) => String(item.name).toLowerCase().includes(frequency))) || plans[0];

      if (subscribe && !plan) {
        toggle.checked = false;
        return;
      }

      // A quantity change still waiting on its debounce is sent along with the switch instead of being lost
      clearTimeout(this.#lineTimers.get(key));
      this.#lineTimers.delete(key);

      const input = line.querySelector('[data-mm-qty-input]');
      const quantity = Math.max(parseInt(input?.value ?? line.dataset.quantity, 10) || 1, 1);

      toggle.disabled = true;
      line.classList.add('is-pending');

      return this.#enqueue(async () => {
        try {
          const cart = await postJSON('cart/change.js', {
            id: key,
            quantity,
            selling_plan: subscribe ? plan.id : null,
            sections: this.sectionId,
            sections_url: window.location.pathname,
          });

          this.#applyCart(cart, cart.sections);
        } catch (error) {
          await this.refresh({ notify: true });
          this.#showLineError(key, error.message);
        }
      });
    }

    /* ------------------------------------------------------------------------------------------------------ adding */

    #addFromButton(button) {
      const variantId = Number(button.dataset.variantId);

      if (!variantId || button.getAttribute('aria-busy') === 'true') {
        return;
      }

      const item = { id: variantId, quantity: 1 };

      if (button.dataset.sellingPlan) {
        item.selling_plan = Number(button.dataset.sellingPlan);
      }

      button.setAttribute('aria-busy', 'true');
      button.querySelector('.mm-cart-spinner')?.removeAttribute('hidden');

      this.addItems([item]).finally(() => {
        button.removeAttribute('aria-busy');
        button.querySelector('.mm-cart-spinner')?.setAttribute('hidden', '');
      });
    }

    addItems(items) {
      return this.#enqueue(async () => {
        try {
          const response = await postJSON('cart/add.js', {
            items,
            sections: this.sectionId,
            sections_url: window.location.pathname,
          });
          const cart = await getCart();

          this.#applyCart(cart, response.sections, 'mm-cart:add');
          return true;
        } catch (error) {
          this.#showError(error.message);
          return false;
        }
      });
    }

    /* --------------------------------------------------------------------------------------------------- rendering */

    async refresh({ notify = false } = {}) {
      try {
        const [sections, cart] = await Promise.all([
          fetch(`${routeUrl('')}?sections=${encodeURIComponent(this.sectionId)}`, { cache: 'no-store' }).then((response) => response.json()),
          getCart(),
        ]);

        if (sections?.[this.sectionId]) {
          this.#render(sections[this.sectionId]);
        }

        this.#cart = cart;
        this.#updateDerived(cart, { sideEffects: true });

        if (notify) {
          this.#notifyTheme(cart, 'mm-cart:refresh');
        }
      } catch (error) {
        console.error('[mm-cart] refresh failed', error);
      }
    }

    #applyCart(cart, sections, baseEvent = 'mm-cart:change') {
      if (sections?.[this.sectionId]) {
        this.#render(sections[this.sectionId]);
      }

      if (!cart) {
        return;
      }

      this.#cart = cart;
      this.#updateDerived(cart, { sideEffects: true, announce: true });
      this.#notifyTheme(cart, baseEvent);
      this.#announce(`Cart updated. ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}.`);
    }

    // Header cart count/dot, the theme's cart cache and other components all listen to `cart:change`
    #notifyTheme(cart, baseEvent) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent, cart, mmCart: true },
      }));
    }

    // Replaces only the regions that changed, so focus, scroll position and running transitions are preserved
    #render(html) {
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector('mm-cart-drawer');

      if (!source) {
        return;
      }

      const focusState = this.#captureFocus();

      this.querySelectorAll('[data-mm-region]').forEach((region) => {
        const next = source.querySelector(`[data-mm-region="${region.dataset.mmRegion}"]`);

        if (next && next.innerHTML !== region.innerHTML) {
          region.innerHTML = next.innerHTML;
        }
      });

      const itemCount = Number(source.dataset.itemCount || 0);
      this.dataset.itemCount = String(itemCount);
      this.classList.toggle('is-empty', itemCount === 0);

      this.#restoreFocus(focusState);
    }

    #captureFocus() {
      const active = document.activeElement;

      if (!(active instanceof HTMLElement) || !this.contains(active) || active.isConnected === false) {
        return null;
      }

      const line = active.closest('[data-mm-cart-line]');

      return {
        element: active,
        variantId: line?.dataset.variantId,
        quantityButton: active.dataset.mmQty,
        isInput: active.hasAttribute('data-mm-qty-input'),
        isSubscribe: active.hasAttribute('data-mm-subscribe-line'),
      };
    }

    #restoreFocus(state) {
      if (!state || state.element.isConnected) {
        return;
      }

      let target = null;

      if (state.variantId) {
        const line = this.querySelector(`[data-mm-cart-line][data-variant-id="${state.variantId}"]`);

        if (state.isSubscribe) {
          target = line?.querySelector('[data-mm-subscribe-line]');
        } else if (state.quantityButton) {
          target = line?.querySelector(`[data-mm-qty="${state.quantityButton}"]`);
        } else if (state.isInput) {
          target = line?.querySelector('[data-mm-qty-input]');
        }
      }

      (target || this.panel).focus({ preventScroll: true });
    }

    /* ------------------------------------------------------------------------------------------ derived UI + rewards */

    #updateDerived(cart, { sideEffects = false, announce = false } = {}) {
      if (!cart) {
        return;
      }

      this.#filterRecommendations(cart);
      this.#togglePromo(cart);

      const actions = this.#updateProgress(cart, announce);

      if (sideEffects && actions) {
        this.#syncRewards(actions);
      }
    }

    #filterRecommendations(cart) {
      const wrapper = this.querySelector('[data-mm-upsell]');

      if (!wrapper) {
        return;
      }

      const variantsInCart = new Set((cart.items || []).map((item) => item.variant_id));
      let visibleCount = 0;

      wrapper.querySelectorAll('[data-upsell-card]').forEach((card) => {
        const variantIds = (card.dataset.variantIds || '').split(',').map(Number);
        const isInCart = variantIds.some((id) => variantsInCart.has(id));

        card.classList.toggle('is-hidden', isInCart);
        visibleCount += isInCart ? 0 : 1;
      });

      wrapper.classList.toggle('is-hidden', visibleCount === 0);
      this.#updateUpsellArrows();
    }

    /* ------------------------------------------------------------------------------------------ recommendation arrows */

    // The recommendations row isn't a re-rendered region, so its track is observed once
    #setupUpsellArrows(signal) {
      const track = this.querySelector('[data-upsell-track]');

      if (!track) {
        return;
      }

      track.addEventListener('scroll', () => this.#updateUpsellArrows(), { passive: true, signal });

      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(() => this.#updateUpsellArrows());
        observer.observe(track);
        signal.addEventListener('abort', () => observer.disconnect());
      } else {
        window.addEventListener('resize', () => this.#updateUpsellArrows(), { signal });
      }

      this.#updateUpsellArrows();
    }

    // Scrolls by the number of whole cards in view (at least one); scroll snapping aligns the result
    #scrollUpsell(direction) {
      const track = this.querySelector('[data-upsell-track]');
      const card = Array.from(track?.querySelectorAll('[data-upsell-card]') || []).find(isRendered);

      if (!track || !card) {
        return;
      }

      const step = card.offsetWidth + (parseFloat(getComputedStyle(track).columnGap) || 0);
      const cardsInView = Math.max(1, Math.floor(track.clientWidth / step));
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      track.scrollBy({ left: direction * cardsInView * step, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    // Arrows show only when the row overflows; the one that can't move further is disabled (and dimmed in CSS)
    #updateUpsellArrows() {
      const track = this.querySelector('[data-upsell-track]');

      if (!track) {
        return;
      }

      const prev = this.querySelector('[data-upsell-arrow="prev"]');
      const next = this.querySelector('[data-upsell-arrow="next"]');
      const maxScroll = track.scrollWidth - track.clientWidth;
      const hasOverflow = maxScroll > 1;
      const atStart = track.scrollLeft <= 1;
      const atEnd = track.scrollLeft >= maxScroll - 1;

      [[prev, atStart, next], [next, atEnd, prev]].forEach(([arrow, isDisabled, other]) => {
        if (!arrow) {
          return;
        }

        // Keep keyboard focus in the drawer when the focused arrow reaches the end of the row
        if (isDisabled && document.activeElement === arrow && other && !other.disabled) {
          other.focus({ preventScroll: true });
        }

        arrow.hidden = !hasOverflow;
        arrow.disabled = !hasOverflow || isDisabled;
      });
    }

    #togglePromo(cart) {
      const variantsInCart = new Set((cart.items || []).map((item) => item.variant_id));

      this.querySelectorAll('[data-mm-set-promo][data-promo-variant-ids]').forEach((promo) => {
        const variantIds = promo.dataset.promoVariantIds.split(',').map(Number);
        promo.classList.toggle('is-hidden', variantIds.some((id) => variantsInCart.has(id)));
      });
    }

    #updateProgress(cart, announce) {
      const root = this.querySelector('#MMCartProgress');

      if (!root) {
        return null;
      }

      // Thresholds are set in the store currency; carts are in the customer's currency
      const rate = Number(window.Shopify?.currency?.rate) || 1;

      const milestones = Array.from(root.querySelectorAll('[data-milestone]')).map((element) => {
        const type = element.dataset.thresholdType === 'items' ? 'items' : 'price';
        const value = parseFloat(String(element.dataset.threshold || '').replace(/[^0-9.]/g, ''));

        return {
          element,
          type,
          threshold: type === 'price' ? value * rate : value,
          giftVariantId: Number(element.dataset.giftVariantId) || 0,
          code: (element.dataset.discountCode || '').trim(),
          message: element.dataset.message || '',
        };
      }).filter((milestone) => Number.isFinite(milestone.threshold) && milestone.threshold > 0);

      if (milestones.length === 0) {
        return null;
      }

      const items = cart.items || [];
      const rewardLines = items.filter((item) => item.properties?.[REWARD_PROPERTY]);
      const rewardCodes = new Set(milestones.map((milestone) => milestone.code.toLowerCase()).filter(Boolean));

      // Rewards granted by the bar never count towards the milestones that granted them
      let cents;
      if (root.dataset.progressBasis === 'discounted') {
        cents = cart.total_price;

        (cart.cart_level_discount_applications || []).forEach((application) => {
          if (rewardCodes.has(String(application.title).toLowerCase())) {
            cents += application.total_allocated_amount;
          }
        });

        items.forEach((item) => {
          if (item.properties?.[REWARD_PROPERTY]) {
            cents -= item.final_line_price;
            return;
          }

          (item.line_level_discount_allocations || []).forEach((allocation) => {
            if (rewardCodes.has(String(allocation.discount_application?.title).toLowerCase())) {
              cents += allocation.amount;
            }
          });
        });
      } else {
        cents = cart.original_total_price - rewardLines.reduce((sum, item) => sum + item.original_line_price, 0);
      }

      const amount = Math.max(cents, 0) / 100;
      const itemCount = Math.max((cart.item_count || 0) - rewardLines.reduce((sum, item) => sum + item.quantity, 0), 0);
      const cartCodes = (cart.discount_codes || []).map((entry) => String(entry.code).toLowerCase());

      const isFirstRun = this.#reachedKeys === null;
      const previouslyReached = this.#reachedKeys || new Set();
      const reachedNow = new Set();
      const actions = { add: [], remove: [], addCodes: [], removeCodes: [] };

      milestones.forEach((milestone, index) => {
        const key = `${index}:${milestone.type}:${milestone.threshold}`;

        milestone.current = milestone.type === 'items' ? itemCount : amount;
        milestone.isReached = milestone.current >= milestone.threshold;
        milestone.element.classList.toggle('is-reached', milestone.isReached);

        if (milestone.isReached) {
          reachedNow.add(key);

          if (announce && !isFirstRun && !previouslyReached.has(key)) {
            this.#celebrate(milestone);
          }
        }

        if (milestone.giftVariantId) {
          const giftLine = rewardLines.find((item) => item.variant_id === milestone.giftVariantId);

          if (milestone.isReached && !giftLine) {
            actions.add.push(milestone.giftVariantId);
          } else if (!milestone.isReached && giftLine) {
            actions.remove.push(giftLine.key);
          }
        }

        if (milestone.code) {
          const isApplied = cartCodes.includes(milestone.code.toLowerCase());

          if (milestone.isReached && !isApplied) {
            actions.addCodes.push(milestone.code);
          } else if (!milestone.isReached && isApplied) {
            actions.removeCodes.push(milestone.code);
          }
        }
      });

      this.#reachedKeys = reachedNow;
      this.#paintFill(root, milestones);

      return actions;
    }

    #paintFill(root, milestones) {
      const fill = root.querySelector('[data-progress-fill]');
      const track = root.querySelector('[data-progress-track]');

      if (!fill || !track) {
        return;
      }

      const trackRect = track.getBoundingClientRect();
      const positions = milestones.map((milestone, index) => {
        const iconRect = milestone.element.querySelector('[data-milestone-icon]')?.getBoundingClientRect();

        // While the drawer is closed nothing has a size: spread the milestones evenly until it opens
        if (!iconRect || !trackRect.width || !iconRect.width) {
          return ((index + 0.5) / milestones.length) * 100;
        }

        return ((iconRect.left + iconRect.width / 2 - trackRect.left) / trackRect.width) * 100;
      });

      const nextIndex = milestones.findIndex((milestone) => !milestone.isReached);
      let percent = 100;

      if (nextIndex !== -1) {
        const next = milestones[nextIndex];
        const previous = milestones[nextIndex - 1];
        const start = previous ? positions[nextIndex - 1] : 0;
        const base = previous && previous.type === next.type ? previous.threshold : 0;
        const ratio = Math.min(Math.max((next.current - base) / ((next.threshold - base) || 1), 0), 1);

        percent = start + ratio * Math.max(positions[nextIndex] - start - FILL_GAP, 0);
      }

      fill.style.width = `${Math.min(Math.max(percent, 0), 100)}%`;
    }

    #celebrate(milestone) {
      milestone.element.classList.remove('is-celebrating');
      void milestone.element.offsetWidth;
      milestone.element.classList.add('is-celebrating');
      setTimeout(() => milestone.element.classList.remove('is-celebrating'), 600);

      const message = this.querySelector('[data-progress-message]');

      if (message && milestone.message) {
        message.textContent = milestone.message;
        message.classList.add('is-visible');
        clearTimeout(this.#messageTimer);
        this.#messageTimer = setTimeout(() => message.classList.remove('is-visible'), MESSAGE_MS);
      }
    }

    #syncRewards({ add, remove, addCodes, removeCodes }) {
      const giftIds = add.filter((id) => !this.#pendingRewardIds.has(id) && !this.#failedRewardIds.has(id));

      if (this.#isSyncingRewards || (giftIds.length === 0 && remove.length === 0 && addCodes.length === 0 && removeCodes.length === 0)) {
        return;
      }

      this.#isSyncingRewards = true;
      giftIds.forEach((id) => this.#pendingRewardIds.add(id));

      this.#enqueue(async () => {
        if (giftIds.length > 0) {
          try {
            await postJSON('cart/add.js', { items: giftIds.map((id) => ({ id, quantity: 1, properties: { [REWARD_PROPERTY]: 'true' } })) });
          } catch (error) {
            // An unavailable gift must not be retried on every cart change
            giftIds.forEach((id) => this.#failedRewardIds.add(id));
            console.error('[mm-cart] reward product could not be added', error);
          }
        }

        if (remove.length > 0) {
          const updates = Object.fromEntries(remove.map((key) => [key, 0]));
          await postJSON('cart/update.js', { updates }).catch((error) => console.error('[mm-cart] reward product could not be removed', error));
        }

        if (addCodes.length > 0 || removeCodes.length > 0) {
          const removeSet = new Set(removeCodes.map((code) => code.toLowerCase()));
          const current = (this.#cart?.discount_codes || []).map((entry) => entry.code);
          const codes = [...new Map([...current, ...addCodes].filter((code) => !removeSet.has(code.toLowerCase())).map((code) => [code.toLowerCase(), code])).values()];

          await postJSON('cart/update.js', { discount: codes.join(',') }).catch((error) => console.error('[mm-cart] reward discount could not be updated', error));
        }
      }).finally(async () => {
        giftIds.forEach((id) => this.#pendingRewardIds.delete(id));
        this.#isSyncingRewards = false;
        await this.refresh({ notify: true });
      });
    }

    /* --------------------------------------------------------------------------------------------------- utilities */

    #enqueue(task) {
      this.#pendingTasks += 1;
      this.classList.add('is-busy');

      const run = this.#queue.then(task).finally(() => {
        this.#pendingTasks -= 1;

        if (this.#pendingTasks === 0) {
          this.classList.remove('is-busy');
        }
      });

      this.#queue = run.catch(() => {});
      return run;
    }

    #showError(message) {
      const target = this.querySelector('#MMCartDrawer-Errors');

      if (!target) {
        return;
      }

      target.textContent = message;
      clearTimeout(this.#errorTimer);
      this.#errorTimer = setTimeout(() => {
        target.textContent = '';
      }, ERROR_MS);
    }

    #announce(message) {
      const status = this.querySelector('#MMCartDrawer-Status');

      if (status) {
        status.textContent = message;
      }
    }
  }

  window.customElements.define('mm-cart-drawer', MMCartDrawer);
})();
